import {
  CalendarOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { AccountActions, TransactionsActions } from '@portal/actions';
import MoneyIcon from '@portal/assets/svg/ic_new_transfer.svg?react';
import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedHeaderDate from '@portal/components/AdvancedHeaderDate/AdvancedHeaderDate';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedRangePicker from '@portal/components/AdvancedRangePicker/AdvancedRangePicker';
import TransactionList from '@portal/components/TransactionList/TransactionList';
import TransactionListSkeleton from '@portal/components/TransactionList/TransactionListSkeleton';
import { extract } from '@portal/config/app.json';
import { DATE_FILTER_TIME_INTERVAL } from '@portal/config/env';
import { getRouteStackPath } from '@portal/config/routes';
import RolesType from '@portal/enum/rolesType';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { TransactionFilter } from '@portal/models/advanced-filters';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { translate } from '@portal/services/i18n';
import { maskMoney } from '@portal/services/masks';
import { StorageItems } from '@portal/services/storage';
import { Tabs, Tooltip } from 'antd';
import { DateTime } from 'luxon';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import './AccountSplit.less';

const defaultTransactionFilter: TransactionFilter = {
  flow: undefined,
  startAt: undefined,
  endAt: undefined,
  pageSize: 10,
  nroUltMovimentoEnv: undefined,
};

const AccountSplit: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    transactions: { transactions },
    loading: { loadTransactionList },
    account: { exportExtractDownloadUrl, accountSplitBalance },
    user: { me: loggedUser },
    theme: { currentTheme },
  } = useReduxState();

  const [transactionFilters, setTransactionFilters] =
    useState<TransactionFilter>(defaultTransactionFilter);
  const [loadingMoreTransactions, setLoadingMoreTransactions] =
    useState<boolean>(false);
  const [lastTransactionId, setLastTransactionId] = useState<
    string | undefined
  >(undefined);
  const [datePicker, setDatePicker] = useState<[Moment, Moment] | null>(null);
  const [showDateRange, setShowDateRange] = useState(false);

  const handleChangeDatePiker = (values: Array<Moment | null> | null) => {
    if (values && values[0] && values[1]) {
      setTransactionFilters((oldFilters) => ({
        ...oldFilters,
        startAt: values[0]?.utc(true).startOf('day').toDate().toISOString(),
        endAt: values[1]?.utc(true).endOf('day').toDate().toISOString(),
      }));
      setDatePicker([values[0], values[1]]);
    }
  };

  const selectedPaymentAccount = loggedUser?.account?.paymentAccounts?.filter(
    (item) => item.type === PaymentAccountType.ACCOUNTSECONDARY
  );

  const getAllTransactionsFiltered = useCallback(() => {
    if (!transactionFilters.startAt || !transactionFilters.endAt) {
      return;
    }

    setLastTransactionId(undefined);

    const transactionParams = {
      ...transactionFilters,
      nroUltMovimentoEnv: undefined,
    };

    dispatch(
      TransactionsActions.getAllTransactions(
        transactionParams,
        loadingMoreTransactions
      )
    );

    dispatch(TransactionsActions.getDailyBalance(transactionParams));
    dispatch(
      AccountActions.getUserBalance(
        selectedPaymentAccount?.[0]?.id,
        false,
        true
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, transactionFilters]);

  const loadMoreTransactions = useCallback(() => {
    if (!transactionFilters.startAt || !transactionFilters.endAt) {
      return;
    }

    setLoadingMoreTransactions(true);

    const transactionParams = {
      ...transactionFilters,
      nroUltMovimentoEnv: lastTransactionId,
    };

    dispatch(TransactionsActions.getAllTransactions(transactionParams, true));

    dispatch(TransactionsActions.getDailyBalance(transactionParams));
  }, [dispatch, transactionFilters, lastTransactionId]);

  useEffect(() => {
    if (selectedPaymentAccount) {
      setHeader(
        StorageItems.PAYMENT_ACCOUNT_ID,
        selectedPaymentAccount[0].id || '',
        'session'
      );
    }
  }, [dispatch, selectedPaymentAccount]);

  useEffect(() => {
    const startDate = DateTime.now()
      .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
      .startOf('day');

    const endDate = DateTime.now()
      .plus({
        days: 5,
      })
      .endOf('day');

    const startAt = startDate.toISODate();
    const endAt = endDate.toISODate();

    setTransactionFilters((oldFilters) => ({
      ...oldFilters,
      startAt,
      endAt,
    }));

    setDatePicker([moment(startDate.toISO()), moment(endDate.toISO())]);
  }, []);

  useEffect(() => {
    if (exportExtractDownloadUrl) {
      const files: string[] = [];

      if (exportExtractDownloadUrl?.pdf) {
        files.push(exportExtractDownloadUrl.pdf);
      }

      if (exportExtractDownloadUrl?.csv) {
        files.push(exportExtractDownloadUrl.csv);
      }

      if (exportExtractDownloadUrl?.ofx) {
        files.push(exportExtractDownloadUrl.ofx);
      }

      if (files.length > 0) {
        let currentIndex = 0;
        const intervalId = setInterval(() => {
          if (currentIndex === files.length - 1) clearInterval(intervalId);
          window.location.href = files[currentIndex];
          currentIndex++;
        }, 1000);
      }

      dispatch(AccountActions.cleanExportDownloadUrl());
    }
  }, [exportExtractDownloadUrl, dispatch]);

  useEffect(() => {
    getAllTransactionsFiltered();
  }, [getAllTransactionsFiltered]);

  useEffect(() => {
    if (transactions && transactions.length > 0) {
      const lastTransaction = transactions[transactions.length - 1];
      if (lastTransaction && 'bankId' in lastTransaction) {
        setLastTransactionId(lastTransaction.bankId);
      }

      setLoadingMoreTransactions(false);
    }
  }, [transactions, transactionFilters.pageSize]);

  const clearFilters = () => {
    const startDate = DateTime.now()
      .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
      .startOf('day');

    const endDate = DateTime.now().plus({ days: 5 }).endOf('day');

    const startAt = startDate.toISODate();
    const endAt = endDate.toISODate();

    setTransactionFilters({
      flow: undefined,
      nroUltMovimentoEnv: undefined,
      pageSize: 10,
      startAt,
      endAt,
    });

    setDatePicker([moment(startDate.toISO()), moment(endDate.toISO())]);

    setLastTransactionId(undefined);

    const params = {
      ...defaultTransactionFilter,
      startAt,
      endAt,
    };

    dispatch(TransactionsActions.getAllTransactions(params, false));
    dispatch(TransactionsActions.getDailyBalance(params));
    dispatch(
      AccountActions.getUserBalance(
        selectedPaymentAccount?.[0]?.id,
        false,
        true
      )
    );
  };

  const renderCalendar = () => {
    let text = translate('PAGES.EXTRACT.BUTTONS.OPEN_CALENDAR');
    let icon = <CalendarOutlined />;

    if (showDateRange) {
      text = translate('PAGES.EXTRACT.BUTTONS.CLOSE_CALENDAR');
      icon = <CloseCircleOutlined />;
    }

    return (
      <Tooltip title={text} placement="top">
        <div
          className={`extract-page__calendar ${showDateRange ? 'open' : ''}`}
          onClick={() => setShowDateRange((prevVisible) => !prevVisible)}
        >
          {icon}
        </div>
      </Tooltip>
    );
  };

  const currentTransactions = transactions;
  const isEmpty = !currentTransactions || currentTransactions.length === 0;

  const renderLimits = () => (
    <div className="account-split__grid">
      <div
        className="account-split__grid__card"
        style={{ borderTop: `4px solid ${currentTheme.borderColor}` }}
      >
        <div className="account-split__grid__card__header">
          <div
            className="account-split__grid__card__icon"
            style={{ background: currentTheme.background }}
          >
            <MoneyIcon />
          </div>
          <p className="account-split__grid__card__label">
            {translate('PAGES.ACCOUNT_SPLIT.LIMITS.TOTAL_LIMIT')}
          </p>
        </div>
        <p className="account-split__grid__card__value">
          {maskMoney((accountSplitBalance?.available ?? '0').toString() ?? '0')}
        </p>
        <p className="account-split__grid__card__description">
          {translate('PAGES.ACCOUNT_SPLIT.LIMITS.TOTAL_LIMIT_DESCRIPTION')}
        </p>
      </div>
    </div>
  );

  const renderExtract = () => {
    return (
      <>
        <div>
          <div className="extract-page__filters">
            <AdvancedHeaderDate
              initialDate={transactionFilters.startAt ?? ''}
              finalDate={transactionFilters.endAt ?? ''}
              longRangeSelectorFormat={extract.longRangeSelectorFormat}
            />
            <div className="extract-page__filters__row">
              {showDateRange ? (
                <div className="extract-page__calendar__wrapper">
                  <AdvancedRangePicker
                    onChange={(value) => handleChangeDatePiker(value)}
                    value={datePicker}
                    disabled={!!loadTransactionList}
                    onFocus={() => setDatePicker(null)}
                    placeholder={[
                      DateTime.now()
                        .minus({ days: DATE_FILTER_TIME_INTERVAL || 30 })
                        .startOf('day')
                        .toFormat(
                          translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                        ),
                      DateTime.now()
                        .plus({ days: 5 })
                        .endOf('day')
                        .toFormat(
                          translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
                        ),
                    ]}
                  />
                </div>
              ) : null}
              {renderCalendar()}
              <AdvancedButton
                text={translate('PAGES.EXTRACT.BUTTONS.SEARCH')}
                onClick={() => getAllTransactionsFiltered()}
                disabled={loadTransactionList}
                endIcon={<SearchOutlined />}
                customStyles={{
                  width: '200px',
                }}
              />
              <AdvancedButton
                text={translate('PAGES.EXTRACT.BUTTONS.CLEAN_FILTERS')}
                onClick={() => clearFilters()}
                endIcon={<ClearOutlined />}
                variant="outlined"
                customStyles={{
                  width: '200px',
                }}
              />
            </div>
          </div>
          <div className="extract-page__content">
            <AccessListControl
              NoAccessComponent={<TransactionListSkeleton active={false} />}
              accessRole={RolesType.TRANSACTION_READ}
            >
              <TransactionList
                longRangeSelectorFormat={extract.longRangeSelectorFormat}
                transactionTime={extract.transactionTime}
                transactionIcon={extract.transactionIcon}
                transactionDetails={extract.transactionIcon}
                isExtract
                data={transactions}
                loading={!!loadTransactionList}
              />
            </AccessListControl>
            {!isEmpty && (
              <div className="extract-page__load-more">
                <AdvancedButton
                  text={translate('PAGES.EXTRACT.BUTTONS.LOAD_MORE')}
                  onClick={loadMoreTransactions}
                  disabled={loadingMoreTransactions}
                  loading={loadingMoreTransactions}
                  variant="outlined"
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            title: translate('PAGES.ACCOUNT_SPLIT.TITLE'),
            active: false,
          },
        ]}
      />
      <div className="account-split">
        <AdvancedPanelContentHeader
          title={translate('PAGES.ACCOUNT_SPLIT.TITLE')}
          testId="account-split-panel"
          description={translate('PAGES.ACCOUNT_SPLIT.DESCRIPTION')}
          goBackAction={() => navigate.back()}
          buttonTitle={translate('APPLICATION.BACK_TO_INIT')}
        />
        {renderLimits()}
        <div className="account-split__tabs-container">
          <div className="account-split__tabs-container__header">
            <div>
              <div className="account-split__grid__card__icon">
                <MoneyIcon />
              </div>
              <h3>{translate('PAGES.ACCOUNT_SPLIT.TABS.HISTORY')}</h3>
            </div>
          </div>
          <Tabs defaultActiveKey="1" type="card">
            <Tabs.TabPane
              tab={translate('PAGES.ACCOUNT_SPLIT.TABS.UTILIZATIONS')}
              key="1"
            >
              {renderExtract()}
            </Tabs.TabPane>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default AccountSplit;
